import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  useTheme
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { normalizeFilesForUpload } from '@/utils/fileUtils';

interface FileDropZoneProps {
  /** Callback when files are dropped or selected */
  onFileUpload: (files: File[]) => void;
  /** Array of accepted file types (e.g., ['.csv', '.txt']) */
  acceptedTypes: string[];
  /** Maximum file size in MB */
  maxFileSize: number;
  /** Whether files are currently being uploaded */
  isUploading?: boolean;
  /** Whether the drop zone is disabled */
  disabled?: boolean;
}

/**
 * Compact file drop zone designed specifically for the FileTab sidebar.
 * Provides drag-and-drop and click-to-browse functionality with visual feedback.
 */
export function FileDropZone({
  onFileUpload,
  acceptedTypes,
  maxFileSize,
  isUploading = false,
  disabled = false
}: FileDropZoneProps) {
  const theme = useTheme();
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileUpload = useCallback((files: File[]) => {
    if (!disabled && !isUploading) {
      // Normalize MIME types for all files before uploading
      const normalizedFiles = normalizeFilesForUpload(files);
      onFileUpload(normalizedFiles);
    }
  }, [onFileUpload, disabled, isUploading]);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!disabled && !isUploading && e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  }, [disabled, isUploading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only hide if leaving the entire component area
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled || isUploading) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      handleFileUpload(droppedFiles);
    }
  }, [handleFileUpload, disabled, isUploading]);

  const handleClick = useCallback(() => {
    if (disabled || isUploading) return;

    // Create file input and trigger click
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = acceptedTypes.join(',');
    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (files.length > 0) {
        handleFileUpload(files);
      }
    };
    input.click();
  }, [handleFileUpload, acceptedTypes, disabled, isUploading]);

  const isInteractive = !disabled && !isUploading;

  return (
    <>
      {/* Drag overlay */}
      {isDragOver && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          border: `2px dashed ${theme.palette.primary.main}`,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          pointerEvents: 'none'
        }}>
          <Typography variant="subtitle1" color="primary.main" sx={{ fontWeight: 'bold' }}>
            Drop files here to upload
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Supports {acceptedTypes.join(', ')} files up to {maxFileSize}MB
          </Typography>
        </Box>
      )}

      {/* Main drop zone - compact design for sidebar */}
      <Box 
        sx={{ 
          p: 2,
          textAlign: 'center',
          minHeight: 120,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: isInteractive ? `2px dashed ${theme.palette.divider}` : 'none',
          borderRadius: 2,
          backgroundColor: isInteractive ? 'action.hover' : 'transparent',
          cursor: isInteractive ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          opacity: disabled ? 0.5 : 1,
          '&:hover': isInteractive ? {
            borderColor: theme.palette.primary.main,
            backgroundColor: 'action.selected'
          } : {}
        }}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <UploadIcon 
          sx={{ 
            fontSize: 36,
            color: 'text.secondary',
            mb: 1,
            opacity: 0.7
          }} 
        />
        <Typography variant="subtitle1" color="text.primary" sx={{ mb: 0.5 }}>
          {isUploading ? 'Uploading files...' : 'Drop files here to upload'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {isUploading ? 'Please wait...' : 'or click to browse files'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Supports {acceptedTypes.join(', ')} files up to {maxFileSize}MB
        </Typography>
      </Box>
    </>
  );
} 